import React, { useState, useEffect, useCallback } from 'react';
import apiConfig from '../../config/api';
import PropertyTab from './tabs/PropertyTab';
import ExistingStateTab from './tabs/ExistingStateTab';
import RightsTab from './tabs/RightsTab';
import ProposedStateTab from './tabs/ProposedStateTab';
import ReportTab from './tabs/ReportTab';
import {
  blankProject,
  makePlanStackItem,
  makePermitItem,
  classifyPlan,
  categoryToRole,
  PLAN_ROLE,
  STATUS,
  field,
} from './state/model';
import { buildExistingDescription } from './state/describe';
import { initialProject, saveProject, clearProject } from './state/storage';
import { computeRights, gatherInputs, inputLabel } from './engine/rights-engine';
import { collectConflicts } from './engine/report';
import { DialogProvider, useDialog } from './components/Dialog';
import './RightsDashboard.css';

const SUB_TABS = [
  { id: 'property', label: 'פרטי הנכס' },
  { id: 'existing', label: 'מצב קיים' },
  { id: 'rights', label: 'זכויות' },
  { id: 'proposed', label: 'מצב מוצע' },
  { id: 'report', label: 'דוח' },
];

function planTypeLabel(status) {
  if (!status) return null;
  const m = {
    approved: 'תכנית מאושרת',
    in_progress: 'תכנית בהליך',
    deposited: 'תכנית מופקדת',
    rejected: 'תכנית שנדחתה',
    cancelled: 'תכנית מבוטלת',
    unknown: 'סטטוס לא ידוע',
  };
  return m[status] || status;
}

function zonesInfluence(zones = []) {
  const z = (zones || [])[0];
  if (!z) return null;
  const bits = [];
  if (z.primary_use) bits.push(z.primary_use);
  if (z.max_far_pct != null) bits.push(`זכויות ${z.max_far_pct}%`);
  if (z.max_height_m != null) bits.push(`גובה ${z.max_height_m} מ'`);
  return bits.join(' · ') || null;
}

function RightsDashboardInner() {
  const dlg = useDialog();
  const [project, setProject] = useState(() => initialProject());
  const [subTab, setSubTab] = useState('property');
  const [autofilling, setAutofilling] = useState(false);
  const [autofillError, setAutofillError] = useState(null);
  const [issuesOpen, setIssuesOpen] = useState(false);

  // Persist the whole live calculation state on every change (browser-only v1).
  useEffect(() => { saveProject(project); }, [project]);

  // Generic immutable updater. `producer` mutates a structural clone; every
  // change bumps updatedAt and (optionally) appends to the audit log, satisfying
  // the spec's "every change is saved" principle.
  const updateProject = useCallback((producer, auditMsg) => {
    setProject((prev) => {
      const next = structuredClone(prev);
      producer(next);
      next.updatedAt = Date.now();
      if (auditMsg) next.auditLog.push({ id: `log_${Date.now()}_${next.auditLog.length}`, at: Date.now(), msg: auditMsg });
      return next;
    });
  }, []);

  // Jump from an issue to the exact spot it refers to: switch tab, then scroll
  // the anchored element into view and flash it. Falls back to just switching
  // the tab when the issue has no specific anchor.
  const goToIssue = useCallback((issue) => {
    setSubTab(issue.tab);
    setIssuesOpen(false);
    if (!issue.anchor) return;
    // Wait for the tab content to mount before locating the anchor.
    setTimeout(() => {
      const el = document.querySelector(`[data-rd-anchor="${issue.anchor}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('rd-flash');
      setTimeout(() => el.classList.remove('rd-flash'), 1800);
    }, 120);
  }, []);

  // Acknowledge an advisory as handled, so it drops off the "needs attention"
  // list (some issues — scanned-doc caveats, base-plan-only remaining — never
  // clear by themselves; the user marks them reviewed to reach zero).
  const dismissIssue = useCallback((id) => {
    updateProject((p) => { p.resolvedIssues = [...(p.resolvedIssues || []), id]; }, 'סומן כטופל');
  }, [updateProject]);

  const resetIssues = useCallback(() => {
    updateProject((p) => { p.resolvedIssues = []; }, 'אופסו סימוני הטיפול');
  }, [updateProject]);

  const newProject = useCallback(async () => {
    const ok = await dlg.confirm({
      title: 'פרויקט חדש',
      message: 'לפתוח פרויקט חדש? הנתונים הנוכחיים יימחקו מהדפדפן.',
      confirmLabel: 'פתח פרויקט חדש',
      cancelLabel: 'ביטול',
      danger: true,
    });
    if (!ok) return;
    clearProject();
    setProject(blankProject());
    setSubTab('property');
  }, [dlg]);

  const applyAutofill = useCallback((data) => {
    updateProject((p) => {
      const plans = data.plans || [];
      // Classify each plan by type, then pick the main rights source. First
      // preference: the plan GovMap's retzefmigrashim layer names for THIS
      // parcel (the authoritative parcel→plan mapping). Fallback: the best
      // detailed approved plan (most recent, then most zones). Comprehensive/
      // national plans stay as ceiling/influence — never main.
      const cats = plans.map((pl) => ({ pl, cat: classifyPlan(pl) }));
      const luMeta = p.property.landUseMeta || null;
      const normRef = (s) => String(s || '').replace(/[\s\-/]/g, '');
      let mainRef = null;
      if (luMeta?.plan) {
        const target = normRef(luMeta.plan);
        const hit = target && cats.find((c) => {
          if (c.cat === 'rejected') return false;
          const ref = normRef(c.pl.plan_ref);
          return ref && (ref.includes(target) || target.includes(ref));
        });
        mainRef = hit ? hit.pl.plan_ref : null;
      }
      if (!mainRef) {
        mainRef = cats
          .filter((c) => c.cat === 'detailed')
          .sort((a, b) =>
            String(b.pl.approval_date || '').localeCompare(String(a.pl.approval_date || '')) ||
            ((b.pl.zones?.length || 0) - (a.pl.zones?.length || 0)))[0]?.pl.plan_ref;
      }

      p.planStack = plans.map((pl) => {
        const cat = classifyPlan(pl);
        const role = pl.plan_ref === mainRef ? PLAN_ROLE.MAIN_RIGHTS : categoryToRole(cat);
        return makePlanStackItem({
          origin: 'auto',
          planRef: pl.plan_ref,
          title: pl.title || pl.plan_ref,
          type: planTypeLabel(pl.status),
          approvalDate: pl.approval_date,
          planStatus: pl.status,
          planUrl: pl.plan_url || null,
          zones: pl.zones || [],
          role,
          influence: zonesInfluence(pl.zones),
          source: 'מאגר תכנון',
        });
      });

      // Float meaningful sources to the top; sink citywide "unclassified" noise.
      // Sorted once at fetch time (not on render) so manual reassignment via the
      // dropdown doesn't make rows jump around under the user.
      const PRIORITY = {
        [PLAN_ROLE.MAIN_RIGHTS]: 0,
        [PLAN_ROLE.MODIFIER]: 1,
        [PLAN_ROLE.POTENTIAL_CEILING]: 2,
        [PLAN_ROLE.INFLUENCE]: 3,
        [PLAN_ROLE.GUIDELINES]: 4,
        [PLAN_ROLE.EXISTING_PERMIT]: 5,
        [PLAN_ROLE.UNCLASSIFIED]: 6,
      };
      p.planStack.sort((a, b) =>
        (PRIORITY[a.role.value] ?? 9) - (PRIORITY[b.role.value] ?? 9) ||
        String(b.approvalDate || '').localeCompare(String(a.approvalDate || '')));

      // Seed rights-engine inputs from the main-rights plan (tagged AUTO,
      // awaiting approval). Zone preference: the zone matching the parcel's
      // land-use designation from GovMap; otherwise a representative zone the
      // user must verify.
      const mainItem = p.planStack.find((it) => it.role.value === PLAN_ROLE.MAIN_RIGHTS);
      if (mainItem) {
        // The main-rights source is identified even when its rules haven't
        // been extracted yet (old scanned plans) — numbers then stay manual.
        p.rights.sourceRef = mainItem.planRef;
        const zones = mainItem.zones || [];
        const designation = luMeta?.designation || null;
        let luZone = designation ? zones.find((z) =>
          (z.zone_name && (z.zone_name.includes(designation) || designation.includes(z.zone_name))) ||
          (z.zone_code && designation.includes(z.zone_code))) : null;
        // OCR'd zone names in old plans are often garbled, so name matching can
        // miss. Fallback: match by use + numeral — "מגורים 2" ↔ residential
        // zone with code/name containing "2" (e.g. א2).
        if (!luZone && designation) {
          const digit = (designation.match(/\d+/) || [])[0];
          const isResidential = /מגורים|דיור/.test(designation);
          if (digit) {
            luZone = zones.find((z) =>
              (isResidential ? (z.primary_use === 'residential' || /מגורים|דיור/.test(z.zone_name || '')) : true) &&
              ((z.zone_code || '').includes(digit) || (z.zone_name || '').includes(digit)));
          }
        }
        const zone = luZone || zones.find((z) => z.max_far_pct != null) || zones[0];
        if (zone) {
          const conf = zone.extraction_confidence ?? null;
          const zoneLabel = zone.zone_code || zone.zone_name || '';
          const note = (luZone
            ? `אזור ${zoneLabel} נבחר לפי ייעוד החלקה (${designation})`
            : 'אזור מייצג מהתכנית — יש לאמת את האזור החל על החלקה')
            + ' · בתכניות ישנות האחוז עשוי לבטא תכסית לקומה ולא שטח כולל — יש לאמת את שיטת החישוב';
          const seed = (key, val) => {
            if (val != null) p.rights[key] = field(val, {
              status: STATUS.AUTO, source: mainItem.planRef, sourceUrl: zone.source_pdf_url || null, confidence: conf, note,
            });
          };
          seed('farPct', zone.max_far_pct);
          seed('maxHeight', zone.max_height_m);
          seed('maxStories', zone.max_stories);

          // Pre-mandate-era plans state rights as per-floor coverage, not total
          // area. When the main plan is old and the numbers fit that pattern,
          // default the calc method accordingly (AUTO — user can switch).
          const year = Number(String(mainItem.approvalDate || '').slice(0, 4));
          if (zone.max_far_pct != null && zone.max_far_pct <= 60 && zone.max_stories && year && year < 1980) {
            p.rights.calcMethod = field('coverage_per_floor', {
              status: STATUS.AUTO, source: mainItem.planRef,
              note: `תכנית משנת ${year} — האחוז פורש כתכסית לקומה (שטח כולל = אחוז × שטח מגרש × קומות). ניתן לשנות את השיטה.`,
            });
          }
        }
      }

      // Dedupe permits by request number — the same בקשה appears as multiple
      // source rows (stages/documents); collapse to one timeline entry and
      // merge their extracted forms. Input is already sorted approval_date desc.
      const byReq = new Map();
      for (const pm of (data.permits || [])) {
        const key = pm.request_number || `id:${pm.permit_id}`;
        if (!byReq.has(key)) byReq.set(key, { ...pm, forms: [...(pm.forms || [])] });
        else byReq.get(key).forms.push(...(pm.forms || []));
      }
      p.permitTimeline = [...byReq.values()].map((pm) => makePermitItem({
        year: pm.approval_date ? String(pm.approval_date).slice(0, 4) : null,
        document: pm.permit_number || pm.request_number,
        action: pm.request_type,
        requestNumber: pm.request_number,
        forms: pm.forms || [],
      }));

      // Seed parcel-specific existing-state summary from the as-built evidence
      // (structured טופס 4 if present, else תיק בניין OCR). All tagged AUTO.
      const ba = data.builtAs || null;
      p.existingState.builtAsRaw = ba;
      const ev = ba && (ba.built_as || ba.building_file_evidence);
      if (ev) {
        const src = ba.building_file_evidence ? 'תיק בניין — פרוטוקול גמר (מסמך סרוק)' : 'טופס 4';
        const conf = ev.confidence ?? null;
        const note = ba.building_file_evidence ? (ev.note || 'טעון אימות מול המסמך הסרוק') : null;
        const evidenceUrl = ba.evidence_url || null;
        const set = (key, val) => {
          if (val != null) p.existingState.summary[key] = { value: val, status: STATUS.AUTO, source: src, sourceUrl: evidenceUrl, confidence: conf, note };
        };
        set('builtArea', ev.built_area_m2);
        set('units', ev.units_count);
        set('rooms', ev.rooms_count);
        set('volume', ev.volume_m3);
        set('storiesAbove', ev.stories_above_ground);
        set('storiesBelow', ev.stories_below_ground);
        set('height', ev.building_height_m);
      }
      // Parcel facts extracted from the OCR'd tik binyan (scoped to this parcel).
      // If the geometric GovMap-polygon area is already in, the document value
      // becomes a cross-check: agreement (≤10%) verifies it; disagreement flips
      // the field to CONFLICT (סותר) for the user to resolve — per the spec,
      // two sources disagreeing is surfaced, never silently averaged.
      const pf = data.parcelFacts || null;
      p.existingState.parcelFacts = pf;
      if (pf && pf.parcel_area_m2 != null) {
        const cur = p.property.parcelArea;
        const curVal = cur && cur.value != null && cur.value !== '' ? Number(cur.value) : null;
        if (Number.isFinite(curVal) && curVal > 0) {
          const diff = Math.abs(curVal - pf.parcel_area_m2) / Math.max(curVal, pf.parcel_area_m2);
          const bfUrl = pf.source_urls?.[0] || null;
          if (diff <= 0.1) {
            p.property.parcelArea = {
              ...cur,
              sourceUrl: cur.sourceUrl || bfUrl,
              note: [`מאומת מול תיק בניין (${pf.parcel_area_m2} מ"ר)`, cur.note].filter(Boolean).join(' · '),
            };
          } else {
            p.property.parcelArea = {
              ...cur,
              status: STATUS.CONFLICT,
              sourceUrl: cur.sourceUrl || bfUrl,
              note: `סתירה בין מקורות: פוליגון GovMap ${curVal} מ"ר מול תיק בניין ${pf.parcel_area_m2} מ"ר — יש להכריע ידנית`,
              // Structured options drive the pick-a-value decision UI.
              conflictOptions: [
                { label: `${curVal} מ"ר · GovMap (פוליגון)`, value: curVal },
                { label: `${pf.parcel_area_m2} מ"ר · תיק בניין`, value: pf.parcel_area_m2 },
              ],
            };
          }
        } else {
          const noteBits = [];
          if (pf.coverage_pct != null) noteBits.push(`תכסית היסטורית ${pf.coverage_pct}%`);
          if (pf.applicable_plans?.length) noteBits.push(`תב"ע: ${pf.applicable_plans.join(', ')}`);
          if (pf.notes) noteBits.push(pf.notes);
          p.property.parcelArea = field(pf.parcel_area_m2, {
            status: STATUS.AUTO,
            source: 'תיק בניין (מסמך סרוק)',
            sourceUrl: pf.source_urls?.[0] || null,
            confidence: pf.confidence,
            note: noteBits.join(' · ') || 'טעון אימות מול המסמך הסרוק',
          });
        }
      }

      // Permit-scoping metadata (how many neighbors' permits were filtered out).
      p.permitsMeta = data.permitsMeta || null;

      // The permits DB covers a subset of the city; when it has nothing for
      // this building, the tik binyan's OCR'd permits become the timeline
      // (tagged AUTO, sourced to the building file).
      if (p.permitTimeline.length === 0 && pf?.permits_timeline?.length) {
        p.permitTimeline = pf.permits_timeline
          .slice()
          .sort((a, b) => (b.year || 0) - (a.year || 0))
          .map((t) => makePermitItem({
            year: t.year ? String(t.year) : null,
            document: 'תיק בניין (מסמך סרוק)',
            action: t.description,
            url: t.url || null,
          }));
      }

      // Auto-generate the existing-state description paragraph.
      p.existingState.description = {
        value: buildExistingDescription(p), status: STATUS.AUTO, source: 'נוצר אוטומטית', confidence: null, note: null,
      };

      // Pull receipt — shown next to the address so the user knows what
      // arrived and when (and that a re-pull is rarely needed).
      p.lastPullAt = Date.now();
      p.lastPullSummary = [
        `${plans.length} תכניות`,
        `${p.permitTimeline.length} היתרים`,
        pf ? 'נתוני תיק בניין' : null,
      ].filter(Boolean).join(' · ');
    }, `משיכת נתונים מהמאגר: ${(data.plans || []).length} תכניות, ${(data.permits || []).length} היתרים`);
  }, [updateProject]);

  // `overrides` carries the just-resolved address values when the pull is
  // triggered straight from address selection (React state hasn't committed
  // yet at that point); the manual re-pull button passes nothing and falls
  // back to the stored project state.
  const runAutofill = useCallback(async (overrides = null) => {
    const o = overrides && Number.isFinite(overrides.x_itm) ? overrides : null;
    const x_itm = o?.x_itm ?? project.property.coords?.x_itm;
    const y_itm = o?.y_itm ?? project.property.coords?.y_itm;
    if (!Number.isFinite(x_itm) || !Number.isFinite(y_itm)) {
      setAutofillError('יש לבחור כתובת תקפה תחילה.');
      return;
    }
    setAutofillError(null);
    setAutofilling(true);
    try {
      const resp = await fetch(`${apiConfig.apiBaseUrl}/api/ask?action=rights-autofill`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          x_itm, y_itm,
          gush: o?.gush ?? (project.property.gush?.value || null),
          helka: o?.helka ?? (project.property.helka?.value || null),
          address: o?.address ?? (project.property.address?.value || null),
          landUsePlan: o?.landUsePlan ?? (project.property.landUseMeta?.plan || null),
        }),
      });
      if (!resp.ok) throw new Error(`server ${resp.status}`);
      const data = await resp.json();
      applyAutofill(data);
    } catch (e) {
      setAutofillError('משיכת הנתונים נכשלה. נסה שוב.');
    } finally {
      setAutofilling(false);
    }
  }, [project.property, applyAutofill]);

  const planStackCount = project.planStack.length;
  const mainRightsSet = project.planStack.some((it) => it.role.value === PLAN_ROLE.MAIN_RIGHTS);

  // Per-tab status + the global "needs attention" list, derived from live state.
  const { tabStatuses, issues, dismissedCount } = deriveAttention(project);
  const conflictN = issues.filter((i) => i.sev === 'סתירה').length;
  const pillTone = issues.length === 0 ? 'ok' : conflictN > 0 ? 'bad' : 'warn';
  const pillText = issues.length === 0
    ? 'הכל טופל'
    : conflictN > 0
      ? `${conflictN} סתירה · ${issues.length - conflictN} לטיפול`
      : `${issues.length} לטיפול`;

  const addr = project.property.address?.value;
  const gush = project.property.gush?.value;
  const helka = project.property.helka?.value;
  const coords = project.property.coords;
  const hasAddress = !!addr;
  // Never render a downstream tab without an address (e.g. after "new project").
  const activeSub = !hasAddress ? 'property' : subTab;

  return (
    <div className="rights-dashboard" dir="rtl">
      <div className="rd-toolhead">
        <div className="rd-toolhead-inner">
          <div className="rd-toolhead-row">
            <div className="rd-toolhead-titles">
              <div className="rd-toolhead-title">
                <h1>בדיקת זכויות</h1>
                <span className="rd-exp-badge">ניסיוני</span>
                <span className="rd-project-name">{project.project.name?.value || 'פרויקט ללא שם'}</span>
              </div>
              {addr && (
                <div className="rd-toolhead-addr">
                  <b>{addr}</b>
                  {gush && <><span className="sep">·</span><span>גוש {gush}</span></>}
                  {helka && <><span className="sep">·</span><span>חלקה {helka}</span></>}
                  {Number.isFinite(coords?.x_itm) && (
                    <><span className="sep">·</span><span dir="ltr" style={{ fontVariantNumeric: 'tabular-nums' }}>ITM {Math.round(coords.x_itm)}, {Math.round(coords.y_itm)}</span></>
                  )}
                </div>
              )}
            </div>
            <div className="rd-toolhead-actions">
              {hasAddress && (
                <button type="button" className={`rd-issues-pill ${pillTone}`} onClick={() => setIssuesOpen((o) => !o)}>
                  <span className="dot" />
                  <span>{pillText}</span>
                </button>
              )}
              <button type="button" className="rd-newproj-btn" onClick={newProject}>פרויקט חדש</button>
            </div>
          </div>
          <nav className="rd-subnav">
            {SUB_TABS.map((t) => {
              const st = tabStatuses[t.id];
              // Address-first: downstream tabs stay locked until a property is chosen.
              const locked = t.id !== 'property' && !hasAddress;
              return (
                <button
                  key={t.id}
                  type="button"
                  className={`rd-subnav-link ${subTab === t.id ? 'active' : ''} ${locked ? 'locked' : ''}`}
                  onClick={() => !locked && setSubTab(t.id)}
                  disabled={locked}
                  title={locked ? 'בחרו כתובת תחילה' : undefined}
                >
                  <span className="rd-subnav-label">{locked && '🔒 '}{t.label}</span>
                  <span className="rd-subnav-status">
                    {locked
                      ? <span>דרושה כתובת</span>
                      : <><span className="dot" style={{ background: st.dot }} /><span>{st.text}</span></>}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {issuesOpen && (
        <>
          <div className="rd-drawer-overlay" onClick={() => setIssuesOpen(false)} />
          <div className="rd-drawer">
            <div className="rd-drawer-head">
              <div>
                <h3>דורש טיפול</h3>
                <div className="rd-drawer-sub">{issues.length} פריטים פתוחים בכל המסכים</div>
              </div>
              <button type="button" className="rd-drawer-close" onClick={() => setIssuesOpen(false)}>✕</button>
            </div>
            <div className="rd-drawer-body">
              {issues.map((i) => (
                <div key={i.id} className="rd-issue">
                  <div className="rd-issue-head">
                    <span className={`rd-chip-tone rd-tone-${i.tone}`}>{i.sev}</span>
                    <span className="rd-issue-tab">{SUB_TABS.find((t) => t.id === i.tab)?.label}</span>
                  </div>
                  <div className="rd-issue-text">{i.text}</div>
                  <div className="rd-issue-actions">
                    <button type="button" className="rd-issue-go" onClick={() => goToIssue(i)}>
                      {i.anchor ? 'הצג במסך ←' : 'עבור למסך ←'}
                    </button>
                    <button type="button" className="rd-issue-resolve" onClick={() => dismissIssue(i.id)} title="סמן כטופל והסר מהרשימה">
                      ✓ תקין · טופל
                    </button>
                  </div>
                </div>
              ))}
              {issues.length === 0 && (
                <div className="rd-drawer-empty">✓ הכל טופל — אפשר להפיק דוח</div>
              )}
            </div>
            <div className="rd-drawer-foot">
              <span>פריטים נסגרים כשמאשרים נתון, מכריעים בסתירה, או מסמנים ״תקין · טופל״.</span>
              {dismissedCount > 0 && (
                <button type="button" className="rd-drawer-reset" onClick={resetIssues}>
                  שחזר {dismissedCount} שסומנו כטופלו
                </button>
              )}
            </div>
          </div>
        </>
      )}

      <div className="rd-inner">
      <div className="rd-body">
        {activeSub === 'property' && (
          <PropertyTab
            project={project}
            updateProject={updateProject}
            onAutofill={runAutofill}
            autofilling={autofilling}
            autofillError={autofillError}
          />
        )}
        {activeSub === 'existing' && (
          <ExistingStateTab project={project} updateProject={updateProject} />
        )}
        {activeSub === 'rights' && (
          <RightsTab project={project} updateProject={updateProject} />
        )}
        {activeSub === 'proposed' && (
          <ProposedStateTab project={project} updateProject={updateProject} />
        )}
        {activeSub === 'report' && (
          <ReportTab project={project} updateProject={updateProject} />
        )}
      </div>

      <div className="rd-statusline">
        <span>{planStackCount} מקורות ב-Plan Stack</span>
        <span>·</span>
        <span>{mainRightsSet ? 'הוגדר מקור זכויות ראשי' : 'טרם הוגדר מקור זכויות ראשי'}</span>
        <span>·</span>
        <span>{project.permitTimeline.length} היתרים</span>
      </div>
      </div>
    </div>
  );
}

export default function RightsDashboard() {
  return (
    <DialogProvider>
      <RightsDashboardInner />
    </DialogProvider>
  );
}

// ---- attention model ----------------------------------------------------------
// Status dots per tab + the aggregated "needs attention" list, all derived from
// field statuses and the engine — items disappear by themselves once the user
// approves a value, resolves a conflict, or fills a missing input.
const DOT = { green: '#10b981', amber: '#f59e0b', red: '#dc2626', gray: '#98A2B3' };
const PROP_KEYS = ['address', 'gush', 'helka', 'subParcels', 'parcelArea', 'landUse', 'localAuthority', 'committee', 'gisUrl', 'gmapsUrl', 'planningDbUrl', 'notes'];
const EXIST_KEYS = ['builtArea', 'units', 'rooms', 'volume', 'storiesAbove', 'storiesBelow', 'height', 'footprint'];
const RIGHTS_KEYS = ['farPct', 'coveragePct', 'maxStories', 'maxHeight', 'densityPerDunam', 'parkingRatio', 'calcMethod'];

function countBy(obj, keys, statuses) {
  return keys.filter((k) => statuses.includes(obj?.[k]?.status)).length;
}

function deriveAttention(project) {
  const prop = project.property;
  const exist = project.existingState.summary;
  const rights = project.rights;

  // Before an address is chosen nothing has been pulled — there's nothing to
  // verify or fix yet. Show a neutral "start here", not "verified" or tasks.
  if (!prop.address?.value) {
    const gray = (text) => ({ text, dot: DOT.gray });
    return {
      tabStatuses: {
        property: gray('התחילו כאן'),
        existing: gray('דרושה כתובת'),
        rights: gray('דרושה כתובת'),
        proposed: gray('דרושה כתובת'),
        report: gray('דרושה כתובת'),
      },
      issues: [],
      dismissedCount: 0,
    };
  }

  const metrics = computeRights(gatherInputs(project));
  const missingInputs = [...new Set(metrics.flatMap((m) => m.missingInputs || []))];
  const remArea = metrics.find((m) => m.key === 'remainingArea');
  const allowedUnits = metrics.find((m) => m.key === 'allowedUnits');

  const propConflicts = countBy(prop, PROP_KEYS, [STATUS.CONFLICT]);
  const propPending = countBy(prop, PROP_KEYS, [STATUS.AUTO, STATUS.ESTIMATED]);
  const existPending = countBy(exist, EXIST_KEYS, [STATUS.AUTO, STATUS.ESTIMATED]);
  const rightsConflicts = countBy(rights, RIGHTS_KEYS, [STATUS.CONFLICT]);
  const rightsPending = countBy(rights, RIGHTS_KEYS, [STATUS.AUTO, STATUS.ESTIMATED]);

  const tabStatuses = {
    property: propConflicts > 0
      ? { text: `סתירה · ${propPending} לאימות`, dot: DOT.red }
      : propPending > 0 ? { text: `${propPending} לאימות`, dot: DOT.amber } : { text: 'אומת', dot: DOT.green },
    existing: existPending > 0 ? { text: `${existPending} לאימות`, dot: DOT.amber } : { text: 'אומת', dot: DOT.green },
    rights: (rightsPending + rightsConflicts + missingInputs.length) > 0
      ? { text: `${rightsPending} לאימות · ${missingInputs.length} חסרים`, dot: rightsConflicts > 0 ? DOT.red : DOT.amber }
      : { text: 'אומת', dot: DOT.green },
    proposed: project.scenarios.length > 0
      ? { text: `${project.scenarios.length} תרחישים · טיוטה`, dot: DOT.gray }
      : { text: 'אין תרחיש', dot: DOT.gray },
    report: { text: 'תצוגה מקדימה', dot: DOT.gray },
  };

  const issues = [];
  for (const c of collectConflicts(project)) {
    issues.push({ id: `conflict-${c.tab}-${c.key}`, sev: 'סתירה', tone: 'red', tab: c.tab || 'property', anchor: `field-${c.key}`, text: `${c.label}: ${c.note || 'הערכים מהמקורות אינם תואמים — יש להכריע.'} ההכרעה תחול על כל המסכים.` });
  }
  if (prop.committee?.status === STATUS.AUTO) {
    issues.push({ id: 'committee-default', sev: 'אזהרה', tone: 'amber', tab: 'property', anchor: 'field-committee', text: 'ועדה מקומית הוגדרה כברירת מחדל לפי הרשות — יש לאמת בערים עם ועדה מרחבית/מחוזית.' });
  }
  if (project.planStack.length > 0) {
    issues.push({ id: 'planstack-zones', sev: 'אזהרה', tone: 'amber', tab: 'property', anchor: 'card-planstack', text: '״זכויות שחולצו״ ב-Plan Stack מציגות את כלל אזורי התכנית — יש לאמת מול התשריט.' });
  }
  if (project.existingState.builtAsRaw?.building_file_evidence) {
    issues.push({ id: 'buildingfile-scan', sev: 'אזהרה', tone: 'amber', tab: 'existing', anchor: 'card-buildingfile', text: 'נתוני מצב קיים חולצו ממסמך סרוק (פרוטוקול גמר) — אין מדידת פנים מאושרת ולא נרשם טופס 4.' });
  }
  if (exist.storiesAbove?.status === STATUS.ESTIMATED) {
    issues.push({ id: 'stories-estimated', sev: 'אזהרה', tone: 'amber', tab: 'existing', anchor: 'field-storiesAbove', text: 'מספר הקומות משוער לפי גובה המבנה — יש לאמת מול היתרים או מדידה.' });
  }
  if (missingInputs.length > 0) {
    issues.push({ id: 'rights-missing', sev: 'חסר', tone: 'amber', tab: 'rights', anchor: 'card-rightsinputs', text: `חסרים לחישוב מלא: ${missingInputs.map(inputLabel).join(', ')} — השלימו מתוך התכנית.` });
  }
  if (remArea?.status === 'ok' && remArea.value < 0) {
    issues.push({ id: 'remaining-base', sev: 'אזהרה', tone: 'amber', tab: 'rights', anchor: 'card-layers', text: 'היתרה מחושבת מול תכנית הבסיס בלבד — הזינו תוספות שכבות (למשל תמ״א 38) במסך הזכויות.' });
  }
  if (project.scenarios.length > 0 && allowedUnits?.status === 'missing') {
    issues.push({ id: 'proposed-density', sev: 'חסר', tone: 'amber', tab: 'proposed', anchor: null, text: 'השוואת יח״ד בפרוגרמה דורשת נתון צפיפות — ההשוואה ״מה נכנס״ חלקית.' });
  }

  const dismissed = project.resolvedIssues || [];
  const live = issues.filter((i) => !dismissed.includes(i.id));
  return { tabStatuses, issues: live, dismissedCount: issues.length - live.length };
}
